import React from 'react';

export interface IStep {
  icon?: React.ReactNode;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
}

interface IStepProps extends IStep {
  index: number;
  stepsCount: number;
  progress: number;
}

const iconSize = 40;
const progressBarWidth = 6;
const activeBackgroundMargin = 6;

const Step = ({
  index,
  stepsCount,
  description,
  progress,
  title,
  icon,
}: IStepProps) => {
  const isFirstStep = index === 0;
  const isLastStep = index + 1 === stepsCount;
  const segmentsCount = stepsCount - 1;

  const progressPerStep = 100 / segmentsCount;

  const currentStepProgress = Math.min(
    ((progress - index * progressPerStep) / progressPerStep) * 100,
    100,
  );

  const isCurrentStepActive =
    isFirstStep || currentStepProgress >= 0 || progress === 100;

  return (
    <>
      <div className="timeline-stepper">
        {!isLastStep && (
          <div className="progress-bar-container">
            <div className="progress-bar" />
          </div>
        )}
        <div className="icon">{icon}</div>
        {isCurrentStepActive && <div className="icon-active" />}
        <div className="step-content">
          <div className="step-title">{title}</div>
          <div className="step-desc">{description}</div>
        </div>
      </div>
      <style jsx>{`
        .timeline-stepper {
          position: relative;
          display: flex;
          border-bottom: var(--space-3);
        }

        .timeline-stepper .icon {
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1;
          height: ${iconSize}px;
          min-width: ${iconSize}px;
          max-width: ${iconSize}px;
          border-radius: 100%;
          background-color: ${isCurrentStepActive
            ? 'var(--clr-primary-1)'
            : 'white'};
          border: ${isCurrentStepActive
            ? ''
            : 'solid 2px var(--clr-primary-1)'};
        }

        .timeline-stepper .icon-active {
          position: absolute;
          height: ${iconSize + activeBackgroundMargin * 2}px;
          min-width: ${iconSize + activeBackgroundMargin * 2}px;
          background: var(--clr-primary-4);
          left: -${activeBackgroundMargin}px;
          top: -${activeBackgroundMargin}px;
          border-radius: 100%;
        }

        .progress-bar-container {
          position: absolute;
          background: var(--clr-secondary-4);
          height: calc(100% - ${iconSize}px + 1px);
          top: ${iconSize - 0.5}px;
          left: calc(${iconSize / 2}px - ${progressBarWidth / 2}px);
          width: ${progressBarWidth}px;
          z-index: 1;
        }

        .progress-bar {
          height: ${currentStepProgress}%;
          background: var(--clr-primary-1);
        }

        .step-content {
          margin-left: 16px;
          margin-bottom: ${isLastStep ? 0 : 'var(--space-5)'};
        }

        .step-title,
        .step-title :global(*) {
          font-weight: 600;
          font-size: var(--font-size-2);
          line-height: 24px;
          border-bottom: 4px;
          margin-bottom: var(--space-1);
        }

        .step-desc,
        .step-desc > :global(*) {
          font-weight: 400;
          font-size: var(--font-size-2);
          color: var(--clr-grey-1);
        }
      `}</style>
    </>
  );
};

interface ITimelineStepperProps {
  progress: number;
  steps: IStep[];
}

const TimelineStepper = ({
  progress = 0,
  steps = [],
}: ITimelineStepperProps) => (
  <>
    {steps.map(({ title, description, icon }, index) => (
      <Step
        key={`timeline_step_${index}`}
        index={index}
        progress={progress}
        stepsCount={steps?.length || 0}
        title={title}
        description={description}
        icon={icon}
      />
    ))}
  </>
);

export default TimelineStepper;
